import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { apiService } from "../services/api";

const emptyEntry = {
    titel: "",
    komponist: "",
    interpret_original: "",
    dauer_sekunden: "",
    bearbeiter: "",
    verlag: "",
    eigenes_werk: false,
    keine_musik: false,
};

const isEntryValid = (entry) => {
    const titel = String(entry.titel || "").trim();
    const komponist = String(entry.komponist || "").trim();
    if (entry.keine_musik) {
        return true;
    }
    return Boolean(titel && (komponist || entry.eigenes_werk));
};

const formatDate = (value) => {
    if (!value) {
        return "";
    }
    const parsed = new Date(`${value}T00:00:00`);
    if (Number.isNaN(parsed.getTime())) {
        return value;
    }
    return new Intl.DateTimeFormat("de-DE", {
        day: "2-digit",
        month: "2-digit",
        year: "2-digit",
    }).format(parsed);
};

const normalizeEntry = (entry) => ({
    ...emptyEntry,
    ...entry,
    dauer_sekunden: entry?.dauer_sekunden ?? "",
});

const parseQuickLine = (line) => {
    const trimmed = String(line || "").trim();
    if (!trimmed) {
        return null;
    }

    const separators = [";", "\t", " - ", " – "];
    for (const separator of separators) {
        if (trimmed.includes(separator)) {
            const [titlePart, ...rest] = trimmed.split(separator);
            const composerPart = rest.join(separator);
            const titel = String(titlePart || "").trim();
            const komponist = String(composerPart || "").trim();
            if (titel && komponist) {
                return normalizeEntry({ titel, komponist });
            }
        }
    }

    return normalizeEntry({ titel: trimmed, komponist: "" });
};

const PublicSetlist = () => {
    const { token } = useParams();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [uploadingExcel, setUploadingExcel] = useState(false);
    const [excelFile, setExcelFile] = useState(null);
    const [eventInfo, setEventInfo] = useState(null);
    const [entries, setEntries] = useState([emptyEntry]);
    const [quickPaste, setQuickPaste] = useState("");
    const [showAdvanced, setShowAdvanced] = useState(false);

    useEffect(() => {
        const load = async () => {
            try {
                setLoading(true);
                const data = await apiService.getPublicSetlist(token);
                setEventInfo(data);
                if (Array.isArray(data.entries) && data.entries.length > 0) {
                    setEntries(data.entries.map((entry) => normalizeEntry(entry)));
                }
            } catch (error) {
                console.error("Error loading public setlist:", error);
                toast.error(error.response?.data?.detail || "Setlist konnte nicht geladen werden");
            } finally {
                setLoading(false);
            }
        };
        if (token) {
            load();
        }
    }, [token]);

    const validCount = useMemo(() => entries.filter(isEntryValid).length, [entries]);

    const updateEntry = (index, field, value) => {
        setEntries((prev) => {
            const next = [...prev];
            next[index] = { ...next[index], [field]: value };
            return next;
        });
    };

    const addEntry = (amount = 1) => {
        setEntries((prev) => [...prev, ...Array.from({ length: amount }, () => ({ ...emptyEntry }))]);
    };

    const removeEntry = (index) => {
        setEntries((prev) => {
            if (prev.length <= 1) {
                return [{ ...emptyEntry }];
            }
            return prev.filter((_, i) => i !== index);
        });
    };

    const addFromQuickPaste = () => {
        const lines = quickPaste
            .split("\n")
            .map((line) => parseQuickLine(line))
            .filter(Boolean);

        if (lines.length === 0) {
            toast.error("Keine verwertbaren Zeilen gefunden. Format: Titel;Komponist");
            return;
        }

        setEntries((prev) => {
            const hasOnlyEmpty = prev.length === 1 && !prev[0].titel && !prev[0].komponist;
            if (hasOnlyEmpty) {
                return lines;
            }
            return [...prev, ...lines];
        });
        setQuickPaste("");
        toast.success(`${lines.length} Zeilen übernommen`);
    };

    const buildPayloadEntries = () => entries
        .filter(isEntryValid)
        .map((entry) => ({
            titel: String(entry.titel || "").trim() || (entry.keine_musik ? "Keine Musik" : ""),
            komponist: entry.keine_musik ? "" : String(entry.komponist || "").trim(),
            interpret_original: String(entry.interpret_original || "").trim() || null,
            dauer_sekunden: entry.dauer_sekunden === "" ? null : Number(entry.dauer_sekunden),
            bearbeiter: String(entry.bearbeiter || "").trim() || null,
            verlag: String(entry.verlag || "").trim() || null,
            eigenes_werk: Boolean(entry.eigenes_werk) && !entry.keine_musik,
            keine_musik: Boolean(entry.keine_musik),
        }));

    const handleSubmit = async (event) => {
        event.preventDefault();
        const payloadEntries = buildPayloadEntries();

        if (payloadEntries.length === 0) {
            toast.error("Bitte mindestens einen Song mit Titel und Komponist eintragen oder „keine Musik“ ankreuzen.");
            return;
        }

        try {
            setSaving(true);
            await apiService.submitPublicSetlist(token, { entries: payloadEntries });
            setEventInfo((prev) => (prev ? { ...prev, setlist_status: "Eingereicht" } : prev));
            toast.success("Setlist eingereicht. Danke!");
        } catch (error) {
            console.error("Error submitting public setlist:", error);
            toast.error(error.response?.data?.detail || "Setlist konnte nicht gespeichert werden");
        } finally {
            setSaving(false);
        }
    };

    const extractFilename = (contentDisposition, fallback) => {
        const match = contentDisposition?.match(/filename="?([^\"]+)"?/i);
        return match?.[1] || fallback;
    };

    const triggerDownload = (blob, filename) => {
        const blobUrl = URL.createObjectURL(blob);
        const anchor = document.createElement("a");
        anchor.href = blobUrl;
        anchor.download = filename;
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        URL.revokeObjectURL(blobUrl);
    };

    const handleTemplateDownload = async () => {
        try {
            const response = await apiService.downloadPublicSetlistTemplate(token);
            const filename = extractFilename(response.headers?.["content-disposition"], "GEMA_Setlist_Vorlage.xlsx");
            triggerDownload(response.data, filename);
        } catch (error) {
            console.error("Error downloading setlist template:", error);
            toast.error("Vorlage konnte nicht heruntergeladen werden.");
        }
    };

    const handleSetlistDownload = async () => {
        try {
            const response = await apiService.downloadPublicSetlistExcel(token);
            const filename = extractFilename(response.headers?.["content-disposition"], "GEMA_Setlist.xlsx");
            triggerDownload(response.data, filename);
        } catch (error) {
            console.error("Error downloading setlist excel:", error);
            toast.error("Setlist konnte nicht heruntergeladen werden.");
        }
    };

    const handleExcelUpload = async () => {
        if (!excelFile) {
            toast.error("Bitte zuerst eine Excel-Datei auswählen.");
            return;
        }
        try {
            setUploadingExcel(true);
            const result = await apiService.uploadPublicSetlistExcel(token, excelFile);
            const importedEntries = Array.isArray(result.entries) ? result.entries : [];
            if (importedEntries.length > 0) {
                setEntries(importedEntries.map((entry) => normalizeEntry(entry)));
            }
            setEventInfo((prev) => (prev ? { ...prev, setlist_status: "Eingereicht" } : prev));
            setExcelFile(null);
            toast.success(`Excel importiert (${result.entries_count || importedEntries.length} Songs).`);
        } catch (error) {
            console.error("Error uploading setlist excel:", error);
            toast.error(error.response?.data?.detail || "Excel-Import fehlgeschlagen.");
        } finally {
            setUploadingExcel(false);
        }
    };

    if (loading) {
        return <div className="mx-auto max-w-3xl p-6 text-center text-gray-600">Setlist wird geladen...</div>;
    }

    if (!eventInfo) {
        return <div className="mx-auto max-w-3xl p-6 text-center text-red-600">Setlist-Link ist ungültig.</div>;
    }

    if (eventInfo.is_setlist_required === false) {
        return (
            <div className="mx-auto max-w-3xl p-6 text-center text-gray-700">
                Für diesen Tarif ist keine Setlist notwendig.
            </div>
        );
    }

    return (
        <div className="mx-auto max-w-5xl p-4 md:p-8">
            <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
                <h1 className="text-2xl font-bold text-gray-900">Setlist einreichen</h1>
                <p className="mt-1 text-sm text-gray-600">{eventInfo.title} | {formatDate(eventInfo.date)}</p>
                {eventInfo.setlist_deadline && (
                    <p className="mt-1 text-sm text-amber-700">Deadline: {formatDate(eventInfo.setlist_deadline)}</p>
                )}
                <p className="mt-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Status: {eventInfo.setlist_status || "Ausstehend"}
                </p>

                <div className="mt-5 grid gap-4 md:grid-cols-2">
                    <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-4">
                        <p className="text-sm font-semibold text-indigo-900">1) Excel (empfohlen)</p>
                        <p className="mt-1 text-xs text-indigo-800">Original-GEMA-Vorlage herunterladen, ausfüllen, hochladen.</p>
                        <div className="mt-3 flex flex-wrap gap-2">
                            <button
                                type="button"
                                onClick={handleTemplateDownload}
                                className="rounded-lg border border-indigo-300 px-3 py-1.5 text-xs font-semibold text-indigo-800 hover:bg-indigo-100"
                            >
                                Vorlage herunterladen
                            </button>
                            <button
                                type="button"
                                onClick={handleSetlistDownload}
                                className="rounded-lg border border-indigo-300 px-3 py-1.5 text-xs font-semibold text-indigo-800 hover:bg-indigo-100"
                            >
                                Aktuelle Setlist als Excel
                            </button>
                        </div>
                        <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center">
                            <input
                                type="file"
                                accept=".xlsx"
                                onChange={(eventValue) => setExcelFile(eventValue.target.files?.[0] || null)}
                                className="text-xs"
                            />
                            <button
                                type="button"
                                onClick={handleExcelUpload}
                                disabled={uploadingExcel}
                                className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700 disabled:opacity-60"
                            >
                                {uploadingExcel ? "Upload läuft..." : "Excel hochladen"}
                            </button>
                        </div>
                    </div>

                    <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                        <p className="text-sm font-semibold text-gray-900">2) Schnell per Copy/Paste</p>
                        <p className="mt-1 text-xs text-gray-600">Eine Zeile pro Song, z.B. `Titel;Komponist`</p>
                        <textarea
                            value={quickPaste}
                            onChange={(event) => setQuickPaste(event.target.value)}
                            rows={5}
                            className="mt-2 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                            placeholder={"Song A;Komponist A\nSong B;Komponist B"}
                        />
                        <button
                            type="button"
                            onClick={addFromQuickPaste}
                            className="mt-2 rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-100"
                        >
                            Zeilen übernehmen
                        </button>
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="mt-6">
                    <div className="mb-3 flex flex-wrap items-center gap-2">
                        <p className="text-sm font-semibold text-gray-900">Manuelle Eingabe ({validCount} gültig)</p>
                        <button
                            type="button"
                            onClick={() => addEntry(1)}
                            className="rounded-lg border border-indigo-200 px-3 py-1.5 text-xs font-semibold text-indigo-700 hover:bg-indigo-50"
                        >
                            + Zeile
                        </button>
                        <button
                            type="button"
                            onClick={() => addEntry(5)}
                            className="rounded-lg border border-indigo-200 px-3 py-1.5 text-xs font-semibold text-indigo-700 hover:bg-indigo-50"
                        >
                            + 5 Zeilen
                        </button>
                        <button
                            type="button"
                            onClick={() => setShowAdvanced((prev) => !prev)}
                            className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-100"
                        >
                            {showAdvanced ? "Zusatzfelder ausblenden" : "Zusatzfelder anzeigen"}
                        </button>
                    </div>

                    <div className="space-y-3">
                        {entries.map((entry, index) => (
                            <div key={`entry-${index}`} className="rounded-lg border border-gray-200 p-3">
                                <div className="grid gap-2 md:grid-cols-[44px_1fr_1fr_1fr_auto] md:items-center">
                                    <span className="text-xs font-semibold text-gray-500">#{index + 1}</span>
                                    <input
                                        type="text"
                                        value={entry.titel || ""}
                                        onChange={(event) => updateEntry(index, "titel", event.target.value)}
                                        placeholder={entry.keine_musik ? "Programmpunkt, z. B. Comedy" : "Titel *"}
                                        className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
                                    />
                                    <input
                                        type="text"
                                        value={entry.komponist || ""}
                                        disabled={entry.keine_musik}
                                        onChange={(event) => updateEntry(index, "komponist", event.target.value)}
                                        placeholder={entry.eigenes_werk ? "Komponist/Autor (dein Name)" : "Komponist/Autor *"}
                                        className="rounded-lg border border-gray-300 px-3 py-2 text-sm disabled:bg-gray-100"
                                    />
                                    <input
                                        type="text"
                                        value={entry.interpret_original || ""}
                                        disabled={entry.keine_musik}
                                        onChange={(event) => updateEntry(index, "interpret_original", event.target.value)}
                                        placeholder="Interpret (bekannt durch)"
                                        className="rounded-lg border border-gray-300 px-3 py-2 text-sm disabled:bg-gray-100"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => removeEntry(index)}
                                        className="rounded-lg border border-red-200 px-3 py-2 text-xs font-semibold text-red-600 hover:bg-red-50"
                                    >
                                        Entfernen
                                    </button>
                                </div>

                                <div className="mt-2 flex flex-wrap gap-4 text-xs text-gray-700">
                                    <label className="inline-flex items-center gap-1">
                                        <input
                                            type="checkbox"
                                            checked={Boolean(entry.eigenes_werk)}
                                            disabled={entry.keine_musik}
                                            onChange={(event) => updateEntry(index, "eigenes_werk", event.target.checked)}
                                        />
                                        Eigenes Werk
                                    </label>
                                    <label className="inline-flex items-center gap-1">
                                        <input
                                            type="checkbox"
                                            checked={Boolean(entry.keine_musik)}
                                            onChange={(event) => updateEntry(index, "keine_musik", event.target.checked)}
                                        />
                                        Keine Musik (z. B. Comedy, Lesung)
                                    </label>
                                </div>

                                {showAdvanced && (
                                    <div className="mt-2 grid gap-2 md:grid-cols-3">
                                        <input
                                            type="number"
                                            min="0"
                                            value={entry.dauer_sekunden || ""}
                                            onChange={(event) => updateEntry(index, "dauer_sekunden", event.target.value)}
                                            placeholder="Dauer (Sekunden)"
                                            className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
                                        />
                                        <input
                                            type="text"
                                            value={entry.bearbeiter || ""}
                                            onChange={(event) => updateEntry(index, "bearbeiter", event.target.value)}
                                            placeholder="Bearbeiter"
                                            className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
                                        />
                                        <input
                                            type="text"
                                            value={entry.verlag || ""}
                                            onChange={(event) => updateEntry(index, "verlag", event.target.value)}
                                            placeholder="Verlag"
                                            className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
                                        />
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>

                    <div className="mt-4">
                        <button
                            type="submit"
                            disabled={saving}
                            className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-60"
                        >
                            {saving ? "Speichern..." : "Setlist einreichen"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default PublicSetlist;
